import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiCalendar, FiCheck, FiClock, FiInbox, FiMapPin, FiUsers, FiX } from 'react-icons/fi';
import {
  clientPortalAdminService,
  type AgencyServiceRequest,
} from '../../services/clientPortalAdminService';
import { getLocale, useI18n } from '../../i18n';
import './ClientRequestsPage.css';

type ResponseStatus = 'acknowledged' | 'scheduled' | 'declined' | 'completed';

const REQUEST_TYPE_LABELS: Record<string, string> = {
  extra_hours: 'Extra guard hours',
  emergency_dispatch: 'Emergency dispatch',
  event_coverage: 'Event coverage',
  site_visit: 'Supervisor site visit',
  other: 'Something else',
};

const STATUS_LABELS: Record<string, string> = {
  submitted: 'Submitted',
  acknowledged: 'Acknowledged',
  scheduled: 'Scheduled',
  declined: 'Declined',
  cancelled: 'Cancelled',
  completed: 'Completed',
};

function formatWhen(value: string | null): string | null {
  if (!value) return null;
  return new Date(value).toLocaleString(getLocale(), {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function ClientRequestsPage() {
  const { t } = useI18n();
  const [requests, setRequests] = useState<AgencyServiceRequest[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [replyFor, setReplyFor] = useState<string | null>(null);
  const [replyNote, setReplyNote] = useState('');
  const [pendingStatus, setPendingStatus] = useState<ResponseStatus>('scheduled');

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      setRequests(await clientPortalAdminService.fetchServiceRequests({ openOnly: !showAll }));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [showAll]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(''), 3000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const respond = async (requestId: string, status: ResponseStatus, note?: string) => {
    setIsBusy(true);
    setError('');
    try {
      await clientPortalAdminService.respondToServiceRequest(requestId, status, note ?? null);
      setReplyFor(null);
      setReplyNote('');
      setToast(t('Response sent to the client.'));
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsBusy(false);
    }
  };

  const urgentCount = useMemo(
    () => requests.filter((row) => row.urgency !== 'routine' && row.status === 'submitted').length,
    [requests],
  );

  return (
    <div className="creq">
      <header className="creq__head">
        <div>
          <h1>{t('Client cover requests')}</h1>
          <p>{t('Extra hours, emergency dispatch and event coverage raised from client portals.')}</p>
        </div>
        <label className="creq__toggle">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(event) => setShowAll(event.target.checked)}
          />
          <span>{t('Include closed requests')}</span>
        </label>
      </header>

      {urgentCount > 0 ? (
        <div className="creq__alert">
          {t('{count} urgent requests are waiting for a response.', { count: urgentCount })}
        </div>
      ) : null}

      {error ? <div className="creq__error">{error}</div> : null}
      {toast ? <div className="creq__toast">{toast}</div> : null}

      {isLoading ? (
        <div className="creq__loading"><div className="spinner" /></div>
      ) : requests.length === 0 ? (
        <div className="creq__empty">
          <FiInbox aria-hidden="true" />
          <strong>{t('No open requests')}</strong>
          <p>{t('Requests raised by clients in their portal land here.')}</p>
        </div>
      ) : (
        <ul className="creq__list">
          {requests.map((request) => {
            const when = formatWhen(request.requested_start_at);
            const until = formatWhen(request.requested_end_at);
            const isOpen = request.status === 'submitted' || request.status === 'acknowledged';
            return (
              <li key={request.id} className={`creq__card creq__card--${request.urgency}`}>
                <div className="creq__card-main">
                  <div className="creq__card-head">
                    <h2>{request.title}</h2>
                    <div className="creq__badges">
                      <span className={`creq__badge creq__badge--${request.status}`}>
                        {t(STATUS_LABELS[request.status] ?? request.status)}
                      </span>
                      {request.urgency !== 'routine' ? (
                        <span className={`creq__badge creq__badge--${request.urgency}`}>
                          {t(request.urgency === 'emergency' ? 'Emergency' : 'Urgent')}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className="creq__facts">
                    <span><FiUsers aria-hidden="true" /> {request.client_contracts?.name ?? t('Client')}</span>
                    {request.sites?.name ? (
                      <span><FiMapPin aria-hidden="true" /> {request.sites.name}</span>
                    ) : null}
                    <span>{t(REQUEST_TYPE_LABELS[request.request_type] ?? 'Request')}</span>
                    <span>{t('{count} officers', { count: request.guards_required })}</span>
                    {when ? (
                      <span><FiCalendar aria-hidden="true" /> {when}{until ? ` – ${until}` : ''}</span>
                    ) : null}
                    <span><FiClock aria-hidden="true" /> {formatWhen(request.created_at)}</span>
                  </div>

                  {request.details ? <p className="creq__details">{request.details}</p> : null}

                  {request.response_note ? (
                    <p className="creq__response">
                      <strong>{t('Your response')}:</strong> {request.response_note}
                    </p>
                  ) : null}
                </div>

                {isOpen ? (
                  <div className="creq__actions">
                    {replyFor === request.id ? (
                      <div className="creq__reply">
                        <select
                          className="form__select"
                          value={pendingStatus}
                          onChange={(event) => setPendingStatus(event.target.value as ResponseStatus)}
                        >
                          <option value="scheduled">{t('Scheduled')}</option>
                          <option value="acknowledged">{t('Acknowledged')}</option>
                          <option value="declined">{t('Declined')}</option>
                          <option value="completed">{t('Completed')}</option>
                        </select>
                        <textarea
                          className="form__input"
                          rows={2}
                          placeholder={t('Message shown to the client')}
                          value={replyNote}
                          onChange={(event) => setReplyNote(event.target.value)}
                        />
                        <div className="creq__reply-actions">
                          <button
                            type="button"
                            className="btn btn--primary btn--sm"
                            disabled={isBusy}
                            onClick={() => void respond(request.id, pendingStatus, replyNote)}
                          >
                            <FiCheck aria-hidden="true" /> {t('Send response')}
                          </button>
                          <button
                            type="button"
                            className="btn btn--secondary btn--sm"
                            onClick={() => { setReplyFor(null); setReplyNote(''); }}
                          >
                            <FiX aria-hidden="true" /> {t('Cancel')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn--primary btn--sm"
                          disabled={isBusy}
                          onClick={() => { setReplyFor(request.id); setPendingStatus('scheduled'); }}
                        >
                          {t('Respond')}
                        </button>
                        {request.status === 'submitted' ? (
                          <button
                            type="button"
                            className="btn btn--secondary btn--sm"
                            disabled={isBusy}
                            onClick={() => void respond(request.id, 'acknowledged')}
                          >
                            {t('Acknowledge')}
                          </button>
                        ) : null}
                      </>
                    )}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
